import { CloseOutlined } from '@ant-design/icons';
import { keepPreviousData,useInfiniteQuery } from '@tanstack/react-query';
import commaNumber from 'comma-number';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import { usePublic } from '@/contexts/PublicContext';
import type {
  ChartFeedbackListReqParams,
  FetchChartFeedbackAll,
  FetchChartFeedbackByCell,
} from '@/entities/chart/api/types';
import { PreprocessFactory } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import AnalysisAskSynclyPanel from '@/features/Analysis/components/AnalysisAskSynclyPanel';
import type { ChartAnalysisFeedbackListModalOptions } from '@/features/Analysis/types';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import { useFeedbackSortState } from '@/features/Feedback/hooks/useFeedbackSortState';
import FeedbackCard from '@/features/Taxonomy/components/FeedbackCard/FeedbackCard';
import useKeymap from '@/hooks/pure/useKeymap';
import service from '@/services/index';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
  type DetailModalOptions,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { detailModalStyle } from '@/styles/style';

type Props = {
  query: string;
  pos: number;
  options: DetailModalOptions<ChartAnalysisFeedbackListModalOptions>;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const ChartAnalysisFeedbackListModal = (props: Props) => {
  const isPublic = usePublic();
  const { workspace_id } = useParams();
  if (!workspace_id) throw new Error('workspace_id is required');
  if (!props.options?.chart) throw new Error('Chart is required');
  const scrollRef = useRef<HTMLDivElement>(null);
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const answer = useHeySynclyStore((state) => state.tempAnswer);
  const chatMap = useHeySynclyStore((state) => state.chatMap);
  const setPopup = usePopup();
  const [chatAlias, setChatAlias] = useState<string | null>(null);
  const [sortStatus, setSortStatus] = useFeedbackSortState();
  const selectedChat = chatAlias ? chatMap.get(chatAlias) : undefined;

  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);

  const requestParams = useMemo(() => {
    const newChartParams = new PreprocessFactory(
      props.options.chart,
    ).runPreprocess();

    let reqParams: Partial<FetchChartFeedbackByCell | FetchChartFeedbackAll> = {
      chart: newChartParams,
    };

    reqParams = {
      ...reqParams,
      displayed_x_items: props.options.displayed_x_items || [],
    } as FetchChartFeedbackAll;

    return reqParams;
  }, [props.options]);

  const fetchFeedbackList = async (
    pageParam: null | string,
    sortStatus: NotNullableSortStatusT,
  ) => {
    const data = await service.chart.fetchChartFeedbackList(
      workspace_id,
      {
        ...requestParams,
        paginate_token: pageParam || undefined,
      } as ChartFeedbackListReqParams,
      sortStatus,
      isPublic,
    );

    const feedbacks = data.feedbacks || [];

    return {
      count: data.count,
      list: feedbacks,
      paginate_token: feedbacks[feedbacks.length - 1]?.paginate_token || null,
    };
  };

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [
        'chart-raw-data-list',
        JSON.stringify(props.options),
        sortStatus.sortBy,
        sortStatus.orderStatus,
      ],
      queryFn: ({ pageParam }) =>
        fetchFeedbackList(pageParam, sortStatus),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage?.paginate_token,
      placeholderData: keepPreviousData,
    });

  const onFeedbackItemClick = (state: SelectedFeedbackState) => {
    if (!state) return;
    if (selectedFeedback?.feedbackAlias === state.feedbackAlias) {
      if (selectedFeedback?.blockAlias === state.blockAlias) {
        setSelectedFeedback(null);
      } else {
        setSelectedFeedback(state);
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.FEEDBACK,
          idx: 1,
          keys: [state.blockAlias],
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      }
    } else {
      setSelectedFeedback(state);
      setNewModal({
        alias: state.feedbackAlias,
        type: DETAIL_MODAL_TYPE.FEEDBACK,
        idx: 1,
        keys: [state.blockAlias],
        options: {
          focusedBlock: state.blockAlias,
        },
      });
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: 0,
    });
  }, [props.query]);

  const flattenData = useMemo(() => {
    return data?.pages
      .flat()
      .map((i) => i.list)
      .flat();
  }, [data?.pages]);

  const total = useMemo(() => {
    return data?.pages[0].count || 0;
  }, [data?.pages]);

  const onCloseClick = () => {
    if (chatAlias && answer?.chat_alias === chatAlias) {
      setPopup({
        type: 'confirm',
        popupTheme: 'negative',
        title: 'Would you like to close this window?',
        message: 'If you close the window, the content will be deleted.',
        confirmText: 'Okay',
        onConfirm: () => {
          props.closeModal();
        },
      });
    } else {
      props.closeModal();
    }
  };

  useKeymap('Escape', onCloseClick);

  const analysis_alias = props.options.analysis_alias;

  return (
    <ModalStyle status={true} pos={props.pos}>
      <VFlex f={1} className="overflow-auto h-full">
        <Flex j="flex-end" a="center" style={{ padding: '16px 32px' }}>
          <Flex g="6px">
            <IconButton
              icon={<CloseOutlined />}
              onClick={onCloseClick}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>
        </Flex>
        <Divider />
        <Space h={24} />
        <VFlex padding="0px 32px" g={6}>
          <Typo type="label1" ellipsis>
            <Typo type="label1" weight="m" className="text-gray-800">
              {props.options.chart.name}
            </Typo>
          </Typo>
        </VFlex>
        <Space h={24} />
        <Flex padding="0px 32px" j="space-between" a="center">
          <Typo type="label2" className="text-gray-700">
            Total feedback : {commaNumber(total)}
          </Typo>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
        </Flex>
        <Space h={24} />
        <List
          isLoading={isLoading}
          data={flattenData}
          className="gap-2 px-8 pb-50 h-full"
          render={(f) => (
            <FeedbackCard
              selectedFeedback={selectedFeedback}
              selectedUnit={null}
              setSelectedFeedback={onFeedbackItemClick}
              key={f.alias}
              feedback={f}
            />
          )}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />

        <AnalysisAskSynclyPanel
          key={JSON.stringify(props.options)}
          socketInfo={{
            chart_payload: {
              chart_id: props.options.chart_alias,
              displayed_x_items: props.options.displayed_x_items,
            },
            chart_alias: props.options.chart_alias,
            dashboard_alias: props.options.dashboard_alias,
            initialState: props.options.initialState,
          }}
          chart_name={props.options.chart.name}
          onSocketConnectChange={setChatAlias}
          analysis_alias={analysis_alias}
          chat={selectedChat}
        />
      </VFlex>
    </ModalStyle>
  );
};



const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
`;
export default ChartAnalysisFeedbackListModal;
